import {
	computed,
	Directive,
	inject,
	input,
	OnInit,
	signal,
} from '@angular/core';
import { ANALYSIS_CURRENCY } from '@marmon/frontend/common';
import {
	centsToDollars,
	coerceCurrency,
	percentageToValue,
	roundNumber,
} from '@marmon/shared/types/common';
import { MarmonIconType } from '../../icon/icons.const';
import { AnalysisCardType } from '../analysis-card/analysis-card.component';

export type BoxDistributionData = {
	value: number;
	percent: string;
	code: AnalysisColorCode;
	id: string;
	total?: number;
};

export type BoxDistribution = {
	label: string;
	id: string;
	data: BoxDistributionData[];
};

export type AnalysisColorCode = 'green' | 'yellow' | 'red' | 'gray';

export type ColorCodeOptions = {
	data?: number[];
	value?: number;
	percent?: number;
	total?: number;
	target?: number;
	box?: number;
};

type BoxIndex = number;
type TargetPercentMap = { [key: BoxIndex]: number };
export type Targets<T> = (data: T[], total: number) => TargetPercentMap | null;

@Directive()
export class BoxDistributionCardBaseComponent<T = number> implements OnInit {
	readonly icon = input.required<MarmonIconType>();
	readonly label = input.required<string>();
	readonly inputData = input.required<T[]>();
	readonly hideTarget = input<boolean>(false);
	readonly tooltip = input<string | null>(null);

	readonly types = input<AnalysisCardType[]>(['percent', 'dollar']);
	readonly type = signal<AnalysisCardType>('percent');

	private readonly activeCurrency = inject(ANALYSIS_CURRENCY, {
		optional: true,
	});

	readonly currency = computed(() => coerceCurrency(this.activeCurrency?.()));

	readonly displayData = computed<BoxDistribution[]>(() =>
		this.getDisplayData(),
	);

	readonly targetLabel = 'analysis.target' as const;
	readonly actualLabel = 'analysis.actual' as const;

	readonly targets: Targets<T> | null = null;

	ngOnInit() {
		this.type.set(this.types()[0]);
	}

	onTypeChange(event: AnalysisCardType | undefined) {
		if (!event) return;
		this.type.set(event);
	}

	/**
	 * The default way we determine what the color code thresholds should be
	 * for the distribution cards.
	 *
	 * @note this can be overridden in cards that extend off of this class to alter
	 * how the color codes are determined.
	 */
	getColorCode(options: ColorCodeOptions): AnalysisColorCode {
		const { target, value } = options;
		if (target == null || value == null) {
			console.error('Color code could not be determined');
			return 'gray';
		}

		const delta = Math.abs(target - value);
		if (delta <= 0.02) return 'green';
		if (delta <= 0.05) return 'yellow';
		return 'red';
	}

	/**
	 * The default way we calculate the display data for the distribution boxes
	 *
	 * @note this can be overridden in cards that extend off of this class to alter
	 * what the calculations will return as.
	 */
	getDisplayData() {
		const data: number[] = this.inputData().map((d) => {
			if (isNaN(d as number)) return 0;
			if (!Number.isFinite(d)) return 0;
			if (typeof d !== 'number') return 0;
			return d;
		});

		const total = data.reduce((a, c) => a + c, 0);

		const type = this.type();

		const getData = (
			value: number,
			options: {
				code?: AnalysisColorCode;
				target?: number;
				box: number;
			},
		) => {
			const { target, box } = options;
			const percent = value / total;

			const code = target
				? (options.code ??
					this.getColorCode({ value: percent, percent, target, box }))
				: 'gray';

			return {
				code,
				total,
				id: crypto.randomUUID(),
				percent: percentageToValue(percent),
				value: type === 'sum' ? value : centsToDollars(value),
			};
		};

		const targets = this.targets ? this.targets(data as T[], total) : null;
		const value: BoxDistribution[] = [
			{
				label: this.actualLabel,
				id: crypto.randomUUID(),
				data: data.map((d, index) => {
					const target = targets ? targets[index] : undefined;
					return getData(d as number, {
						target,
						box: index,
					});
				}),
			},
		];

		if (targets) {
			value.push({
				label: this.targetLabel,
				id: crypto.randomUUID(),
				data: data.map((_, index) =>
					getData(roundNumber(total * targets[index], 2), {
						code: 'gray',
						box: index,
					}),
				),
			});
		}

		return value;
	}
}
